import { useEffect, useState } from 'react'
import {
  api,
  type DeliveryReport,
  type ModelInfo,
  type SchedulerStatus,
  type SesState,
  type SettingsPayload,
  type UnsubscribeReport,
} from '../api'
import { useNotice } from '../useNotice'

/**
 * Settings — the operator-editable tier, and nothing else.
 *
 * Three tiers of configuration exist. Deployment values live in .env; copy,
 * prompts, cooldowns and schedules live on each campaign; what is left is this
 * screen — plus a read-only view of the scheduler, which is the one process
 * that can send without a person present.
 *
 * Two kinds of .env value, treated differently on purpose. SECRETS are
 * reported by *presence* only, never by value — an endpoint that returns an
 * API key to a browser is a leak regardless of who is authenticated. The
 * sender identity and unsubscribe base are NOT secrets, and their values are
 * shown, because "is something set" cannot answer the question actually asked
 * of them: which mailbox will receive the replies this copy asks for.
 *
 * Neither is editable here. Both used to be, as text fields nothing read.
 *
 * The model catalogue is fetched live rather than stored. Prices and
 * availability change, and a stale local copy would be worse than a slow
 * screen — only the chosen slug is persisted.
 */
export function SettingsTab() {
  const [data, setData] = useState<SettingsPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useNotice()

  const load = async () => {
    try {
      setError(null)
      setData(await api.settings())
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const save = async (key: string, value: string) => {
    setSaved(null)
    try {
      await api.setSetting(key, value)
      setSaved(`${key} saved`)
      await load()
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  if (error) return <div className="error">{error}</div>
  if (!data) return <div className="loading">Loading settings…</div>

  const value = (key: string) => data.settings.find((s) => s.key === key)?.value ?? ''

  return (
    <>
      {saved && <div className="notice">{saved}</div>}

      <SchedulerPanel />

      <DeliveryPanel />

      <ModelPicker current={value('llm.default_model')} onPick={(slug) => save('llm.default_model', slug)} />

      <div className="panel">
        <h2>Sending</h2>
        <div className="panel-body">
          <p className="hint">
            Set in the environment, and <strong>not editable here</strong>. These were once two
            text fields on this screen and nothing read either of them — a value typed in showed
            as configured while dispatch used the environment's. The sender is shared with Account
            API so DKIM already aligns (D31), which only stays true while there is one of it.
          </p>
          <dl className="facts">
            <dt>Sender identity</dt>
            <dd>
              {data.deployment?.sender_identity ? (
                <span className="mono">{data.deployment.sender_identity}</span>
              ) : (
                <span className="badge bad">not set — REACHOUT_EMAIL_FROM</span>
              )}
            </dd>
            <dt>Unsubscribe base</dt>
            <dd>
              {data.deployment?.unsubscribe_base ? (
                <span className="mono">{data.deployment.unsubscribe_base}</span>
              ) : (
                <span className="badge bad">not set — REACHOUT_UNSUBSCRIBE_BASE</span>
              )}
            </dd>
          </dl>
          <p className="hint">
            The sender's value is shown rather than just whether it is set, because it decides
            whether copy asking for a reply can be approved at all — a no-reply address turns
            "Reply and tell us" into a question nobody can answer.
          </p>
        </div>
      </div>

      <SendGates settings={data} />
      <SesEvidence />

      <div className="panel">
        <h2>Secrets</h2>
        <div className="panel-body">
          <p className="hint">
            Configured in the environment, not here. Only whether each is set is shown — the values
            never leave the server.
          </p>
          <dl className="facts">
            {Object.entries(data.secrets_configured).map(([name, present]) => (
              <div key={name} style={{ display: 'contents' }}>
                <dt className="mono">{name}</dt>
                <dd>
                  <span className={`badge ${present ? 'ok' : 'bad'}`}>
                    {present ? 'set' : 'not set'}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </>
  )
}

function ModelPicker({ current, onPick }: { current: string; onPick: (slug: string) => void }) {
  const [models, setModels] = useState<ModelInfo[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [freeOnly, setFreeOnly] = useState(false)
  const [loading, setLoading] = useState(false)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.models({ search: search || undefined, free_only: freeOnly })
      setModels(res.models)
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="panel">
      <h2>
        Refinement model
        {current ? <span className="count mono">{current}</span> : <span className="count">none chosen</span>}
      </h2>
      <div className="panel-body">
        {!current && (
          <div className="notice">
            No model is configured, so refinement will refuse rather than guess. Pick one below.
          </div>
        )}
        {error && <div className="error">{error}</div>}

        <p className="hint">
          Fetched live from OpenRouter. Prices are per million tokens; a blank price means the model
          routes to others and the cost depends on where it lands.{' '}
          <span className="mono">:batch</span> models are excluded — they are served only by the
          Batch API, which this build does not implement, so offering one would be offering a model
          that cannot run.
        </p>

        <div className="row">
          <input
            type="text"
            placeholder="search by name or slug"
            value={search}
            style={{ maxWidth: 320 }}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void load()}
          />
          <label className="check">
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={freeOnly}
              onChange={(e) => setFreeOnly(e.target.checked)}
            />
            Free only
          </label>
          <button onClick={() => void load()} disabled={loading}>
            {loading ? 'Loading…' : models ? 'Refresh' : 'Browse models'}
          </button>
        </div>

        {models && (
          <table>
            <thead>
              <tr>
                <th>Model</th>
                <th className="num">In $/M</th>
                <th className="num">Out $/M</th>
                <th className="num">Context</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {models.slice(0, 60).map((m) => (
                <tr key={m.slug} className={m.slug === current ? 'selected' : undefined}>
                  <td className="mono">
                    {m.slug} {m.free && <span className="badge ok">free</span>}
                  </td>
                  <td className="num">{m.prompt_per_million ?? '—'}</td>
                  <td className="num">{m.completion_per_million ?? '—'}</td>
                  <td className="num">{m.context_length?.toLocaleString() ?? '—'}</td>
                  <td>
                    {m.slug === current ? (
                      <span className="badge ok">in use</span>
                    ) : (
                      <button onClick={() => onPick(m.slug)}>Use</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

/**
 * What SES itself says, read live.
 *
 * Shown next to the deliverability attestation on purpose: that judgement is
 * the one thing here a person still has to make, and it should be made
 * against facts. Reputation status, whether DKIM is signing, whether bounce
 * and complaint feedback has anywhere to go — those are the inputs, and
 * before this they were simply absent.
 */
function SesEvidence() {
  const [state, setState] = useState<SesState | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        setState((await api.sendReadiness()).ses ?? null)
      } catch (err) {
        setError(String((err as Error).message))
      }
    })()
  }, [])

  if (error) return <div className="error">{error}</div>
  if (!state) return null

  const evidence = state.evidence ?? {}
  const dests = (evidence.event_destinations ?? []) as { name?: string; events?: string[] }[]

  // `undefined` means the check never got that far — the lookup before it
  // failed and returned early. Rendering String(undefined) put the literal
  // word "undefined" on the screen where a verdict belongs, which reads as a
  // bug in the panel rather than a fact about SES.
  const flag = (value: unknown) =>
    value === undefined ? (
      <span className="badge bad">not checked</span>
    ) : (
      <span className={`badge ${value ? 'ok' : 'bad'}`}>{String(value)}</span>
    )

  return (
    <div className="panel">
      <h2>
        What SES reports
        <span className="count mono">
          {state.region ?? '—'}
          {state.checked ? '' : ' · not reachable'}
        </span>
      </h2>
      <div className="panel-body">
        {state.problems.length > 0 && (
          <div className="error">
            <strong>Blocking:</strong>
            <ul className="bullets">
              {state.problems.map((p) => (
                <li key={p} style={{ fontSize: 12 }}>{p}</li>
              ))}
            </ul>
          </div>
        )}
        <dl className="facts">
          <dt>Sending enabled</dt>
          <dd>{flag(evidence.sending_enabled)}</dd>
          <dt>Production access</dt>
          <dd>{flag(evidence.production_access)}</dd>
          <dt>Enforcement</dt>
          <dd>
            <span className={`badge ${evidence.enforcement_status === 'HEALTHY' ? 'ok' : 'bad'}`}>
              {String(evidence.enforcement_status ?? '—')}
            </span>
          </dd>
          <dt>Sender verified</dt>
          <dd>{flag(evidence.sender_verified)}</dd>
          <dt>DKIM signing</dt>
          <dd>
            {flag(evidence.dkim_signing)}{' '}
            <span className="hint">
              status {String(evidence.dkim_status ?? '—')} — unsigned mail is a deliverability
              risk for a resumed sender, and this is evidence for the attestation above rather
              than a blocker, because DKIM may be configured on a different SES account than
              the one these credentials reach.
            </span>
          </dd>
          <dt>Configuration set</dt>
          <dd>
            <span className="mono">{state.config_set ?? '—'}</span>{' '}
            {evidence.config_set_exists === undefined ? (
              <span className="badge bad">does not exist</span>
            ) : (
              flag(evidence.config_set_exists)
            )}
          </dd>
          <dt>Sets that do exist</dt>
          <dd className="mono">
            {((evidence.configuration_sets ?? []) as string[]).join(', ') || '—'}
          </dd>
          <dt>Feedback destinations</dt>
          <dd>
            {dests.length === 0 ? (
              <span className="badge bad">none</span>
            ) : (
              dests.map((d) => (
                <div key={d.name} className="mono" style={{ fontSize: 12 }}>
                  {d.name}: {(d.events ?? []).join(', ')}
                </div>
              ))
            )}
          </dd>
        </dl>
      </div>
    </div>
  )
}

function SendGates({ settings }: { settings: SettingsPayload }) {
  const gates = settings.settings.filter((s) => s.key.startsWith('gate.'))
  return (
    <div className="panel">
      <h2>Send prerequisites</h2>
      <div className="panel-body">
        <p className="hint">
          <strong>One</strong> thing no code can check for itself: whether deliverability has
          recovered after the sending gap. That is not in any API. It starts at{' '}
          <span className="mono">unknown</span>, and unknown counts as blocked. Recording it is a
          person stating they checked, with evidence — never a way to skip the check, and
          deliberately not editable from this screen.
        </p>
        <p className="hint">
          The SES configuration set used to be a second attestation, on the grounds that nothing
          here could inspect an AWS account. That held only while there were no credentials.
          There are, so it is <strong>checked against SES on every read</strong> and attesting it
          is now refused: a note from somebody who looked last month must not override a live
          answer. What AWS reports is below.
        </p>
        <dl className="facts">
          {gates.map((g) => {
            const state = g.value.split(' — ')[0]
            return (
              <div key={g.key} style={{ display: 'contents' }}>
                <dt className="mono">{g.key.replace('gate.', '')}</dt>
                <dd>
                  <span className={`badge ${state === 'pass' ? 'ok' : 'bad'}`}>{state}</span>{' '}
                  {g.value.includes(' — ') && (
                    <span className="hint">
                      {g.value.split(' — ').slice(1).join(' — ')}
                    </span>
                  )}
                </dd>
              </div>
            )
          })}
        </dl>
      </div>
    </div>
  )
}


/**
 * Delivery and opt-outs — the half of the loop that comes back.
 *
 * Two numbers here are obligations rather than statistics.
 *
 * `configured` false is the one that matters most and used to be invisible.
 * The send ladder refuses to send unless the SES configuration set forwards
 * bounces somewhere; if nothing is RECEIVING them, `account_delivery` is empty,
 * and an empty table means a hard bounce stops nothing — we would mail the
 * same dead address on every cycle, for ever, and no screen would say so.
 *
 * `unreconciled` is the other. Reachout honours every unsubscribe here
 * immediately, and cannot write to Mailster or AccountConfig.Newsletter
 * because its CRM connection is read-only. Until somebody pushes them across,
 * every other system that mails these people still believes they are
 * subscribed. These endpoints existed with no consumer at all until now.
 */
function DeliveryPanel() {
  const [delivery, setDelivery] = useState<DeliveryReport | null>(null)
  const [unsubs, setUnsubs] = useState<UnsubscribeReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const [d, u] = await Promise.all([api.delivery(), api.unsubscribes()])
        setDelivery(d)
        setUnsubs(u)
      } catch (err) {
        setError(String((err as Error).message))
      }
    })()
  }, [])

  if (error) return <div className="error">{error}</div>
  if (!delivery || !unsubs) return null

  const suppressing = delivery.suppressing
  return (
    <div className="panel">
      <h2>
        Delivery{' '}
        <span className={`badge ${delivery.configured ? 'ok' : 'bad'}`}>
          {delivery.configured ? 'receiving' : 'not receiving'}
        </span>
      </h2>
      <div className="panel-body">
        {!delivery.configured && (
          <p className="hint">
            <strong>No SES event topic is configured</strong>, so nothing records what happens
            to a send. Bounces and complaints are what stop us mailing a dead address a second
            time — without them that check matches nobody and every cycle tries again. Set{' '}
            <span className="mono">REACHOUT_SES_SNS_TOPIC_ARN</span> and subscribe the topic to{' '}
            <span className="mono">/ses/events</span>; see deploy/README.md.
          </p>
        )}
        <dl className="facts">
          <dt>Events</dt>
          <dd>
            {delivery.events === 0
              ? 'none recorded'
              : delivery.by_type.map((r) => `${r.count} ${r.event_type}`).join(' · ')}
          </dd>
          <dt>Never mail again</dt>
          <dd>
            {suppressing.hard_bounced} hard bounce(s), {suppressing.complained} complaint(s)
            {suppressing.soft_bounced > 0 && `, ${suppressing.soft_bounced} in soft-bounce cooldown`}
          </dd>
          <dt>Unsubscribed</dt>
          <dd>
            {unsubs.total} total
            {unsubs.unreconciled > 0 && (
              <>
                {' — '}
                <strong>{unsubs.unreconciled} not yet in Mailster or AccountConfig</strong>
                <div className="hint caption">
                  Reachout refuses to mail these already. Every other system that mails them
                  still believes they are subscribed, until somebody pushes them across.
                </div>
              </>
            )}
          </dd>
        </dl>
        <p className="hint">{delivery.note}</p>
      </div>
    </div>
  )
}


/**
 * The scheduler, from the only evidence the GUI has: its job rows.
 *
 * It runs as a systemd timer on the production box (D95) and reads its OWN
 * environment, so the kill switch shown in the header says nothing about it.
 * `stale` on a five-minute timer means the timer is not firing.
 */
function SchedulerPanel() {
  const [state, setState] = useState<SchedulerStatus | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        setState(await api.schedulerStatus())
      } catch (err) {
        setError(String((err as Error).message))
      }
    })()
  }, [])

  if (error) return <div className="error">{error}</div>
  if (!state) return null

  const last = state.last_tick
  const detail = (last?.detail ?? {}) as { ticked?: unknown[]; dispatched?: unknown[]; refused?: unknown[] }

  return (
    <div className="panel">
      <h2>
        Scheduler{' '}
        {last ? (
          <span className={`badge ${state.stale ? 'bad' : 'ok'}`}>{state.stale ? 'stale' : 'ticking'}</span>
        ) : (
          <span className="badge parked">never ran</span>
        )}
      </h2>
      <div className="panel-body">
        <dl className="facts">
          <dt>Last pass</dt>
          <dd>
            {last ? (
              <>
                {new Date(last.started_at).toLocaleString()} — {last.status}
                {detail.ticked ? ` · ${detail.ticked.length} campaign(s) ticked` : ''}
                {detail.dispatched ? `, ${detail.dispatched.length} dispatched` : ''}
                {detail.refused?.length ? `, ${detail.refused.length} refused` : ''}
              </>
            ) : (
              'no pass recorded yet'
            )}
          </dd>
          <dt>Overdue</dt>
          <dd>{state.overdue_campaigns} enabled campaign(s) past their next run</dd>
          <dt>Armed</dt>
          <dd>
            {state.armed_campaigns} campaign(s) armed for automatic dispatch
            {state.armed_campaigns > 0 && (
              <div className="hint caption">
                These send without a person present, on the scheduler's next pass, if its own
                kill switch is on and every gate clears.
              </div>
            )}
          </dd>
        </dl>
        <p className="hint">{state.note}</p>
      </div>
    </div>
  )
}
